/**
 * Server-state hooks of the work orders. Queries render their own error states (no global toast); a write
 * replaces the card in the cache with the server's answer and invalidates the lists and the feed.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { workOrderKeys } from '../api/queryKeys';
import * as api from '../api/workOrdersApi';

import type { ListParams, WorkOrderCard } from '../types/workOrderTypes';

const QUIET = { suppressGlobalError: true } as const;

export function useWorkOrders(params: ListParams, enabled = true) {
  return useQuery({ queryKey: workOrderKeys.list(params), queryFn: () => api.fetchWorkOrders(params), enabled, placeholderData: keepPreviousData, meta: QUIET });
}

export function useWorkOrderCounts() {
  return useQuery({ queryKey: workOrderKeys.counts(), queryFn: api.fetchCounts, meta: QUIET });
}

export function useWorkOrder(id: string | undefined) {
  return useQuery({
    queryKey: workOrderKeys.card(id ?? ''),
    queryFn: () => api.fetchWorkOrder(id as string),
    enabled: id !== undefined,
    meta: QUIET,
  });
}

export function useStatusOptions(id: string, enabled: boolean) {
  return useQuery({ queryKey: workOrderKeys.statusOptions(id), queryFn: () => api.fetchStatusOptions(id), enabled, staleTime: 0, meta: QUIET });
}

export function useFeed(id: string) {
  return useQuery({ queryKey: workOrderKeys.feed(id), queryFn: () => api.fetchFeed(id), meta: QUIET });
}

export function useViews() {
  return useQuery({ queryKey: workOrderKeys.views(), queryFn: api.fetchViews, meta: QUIET });
}

export function useUsersLookup(enabled = true) {
  return useQuery({ queryKey: workOrderKeys.lookup('users', ''), queryFn: api.lookupUsers, enabled, staleTime: 5 * 60_000, meta: QUIET });
}

export function useVehicleLookup(q: string) {
  return useQuery({
    queryKey: workOrderKeys.lookup('vehicles', q),
    queryFn: () => api.lookupVehicles(q),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
    meta: QUIET,
  });
}

export function useCustomerLookup(q: string) {
  return useQuery({
    queryKey: workOrderKeys.lookup('customers', q),
    queryFn: () => api.lookupCustomers(q),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
    meta: QUIET,
  });
}

/** A card write: the answer replaces the cached card; lists, counts and the feed refetch. */
export function useCardMutation<V>(id: string, fn: (vars: V) => Promise<WorkOrderCard>) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: QUIET,
    mutationFn: fn,
    onSuccess: (card) => {
      queryClient.setQueryData(workOrderKeys.card(id), card);
      void queryClient.invalidateQueries({ queryKey: workOrderKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: workOrderKeys.counts() });
      void queryClient.invalidateQueries({ queryKey: workOrderKeys.feed(id) });
      void queryClient.invalidateQueries({ queryKey: workOrderKeys.statusOptions(id) });
      // A card write may stop timers server-side (a performer taken off a work, a cancelled work or
      // order): the cached time must not keep counting a timer that is no longer running.
      void queryClient.invalidateQueries({ queryKey: workOrderKeys.time(id) });
      void queryClient.invalidateQueries({ queryKey: workOrderKeys.running() });
    },
  });
}

export function useCreateWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: QUIET,
    mutationFn: api.createWorkOrder,
    onSuccess: (card) => {
      queryClient.setQueryData(workOrderKeys.card(card.id), card);
      void queryClient.invalidateQueries({ queryKey: workOrderKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: workOrderKeys.counts() });
    },
  });
}

export function useAddComment(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: QUIET,
    mutationFn: (body: string) => api.addComment(id, body),
    onSuccess: (feed) => queryClient.setQueryData(workOrderKeys.feed(id), feed),
  });
}

export function useSaveView() {
  const queryClient = useQueryClient();
  return useMutation({ meta: QUIET, mutationFn: api.saveView, onSuccess: () => queryClient.invalidateQueries({ queryKey: workOrderKeys.views() }) });
}

export function useDeleteView() {
  const queryClient = useQueryClient();
  return useMutation({ meta: QUIET, mutationFn: api.deleteView, onSuccess: () => queryClient.invalidateQueries({ queryKey: workOrderKeys.views() }) });
}
